import type { App } from "../app/App";
import { RuntimeGS, RuntimeScopeGS } from "../app/RuntimeGS";
import { StateConfigurationGS } from "./StateConfigurationGS";
import type { StateConstructor } from "./StateContractsGS";
import { StateRunResultEnum } from "./StateContractsGS";
import type { StateGS } from "./StateGS";

export interface StateRunOptionsGS {
    readonly withHistory?: boolean;
    readonly notFound?: boolean;
    readonly fromUrl?: boolean;
    /** Original browser/request URL used to resolve the state, including its fragment. */
    readonly requestedUrl?: string;
}

export interface StateTransitionGS {
    readonly constructor: StateConstructor;
    readonly args?: any;
    readonly url: string;
}

interface StateExecutionScopeGS {
    activeState?: StateGS;
    runningState?: StateGS;
    afterStartState?: StateGS;
    transition?: StateTransitionGS;
}

/** Environment adapter behind the public StateGS facade. */
export abstract class StateRuntimeGS {
    protected constructor(
        protected readonly app: App,
        public readonly configuration: StateConfigurationGS
    ) {
    }

    public get activeState(): StateGS | undefined {
        return StateRuntimeStoreGS.execution().activeState;
    }

    public get urlStateArgName(): string { return this.configuration.urlStateArgName; }
    public get defaultStateName(): string { return this.configuration.DefaultStateName; }
    public get state404Name(): string { return this.configuration.State404Name; }

    public buildStateUrl(stateName: string, args: Record<string, string> = {}): string {
        return this.configuration.buildStateUrl(stateName, args);
    }

    public buildUrlForState(state: StateGS, extraArgs?: Record<string, string>): string {
        return this.configuration.buildUrlForState(state, extraArgs);
    }

    protected ensureTransitionAllowed(targetStateName: string): void {
        const source = StateRuntimeStoreGS.execution().afterStartState;
        if (!source) return;
        throw new Error(
            `State '${targetStateName || "(empty)"}' cannot start while state '${source.name}' is executing afterStart. `
            + "Choose the transition in preStart/start or trigger it after afterStart completes."
        );
    }

    public async runByName(
        stateName: string,
        args?: any,
        options: StateRunOptionsGS = {}
    ): Promise<StateRunResultEnum> {
        const requestedName = stateName || this.configuration.DefaultStateName;
        let constructor = this.configuration.getStateConstructor(requestedName);
        let notFound = options.notFound === true;
        if (!constructor && this.configuration.isStatePlaceholder(requestedName))
            return StateRunResultEnum.Failed;
        if (!constructor) {
            constructor = this.configuration.getStateConstructor(this.configuration.State404Name);
            notFound = true;
        }
        if (!constructor) return StateRunResultEnum.Failed;
        return this.run(constructor, args, { ...options, notFound });
    }

    public runByUrlArgs(
        args: Record<string, string>,
        options: StateRunOptionsGS = {}
    ): Promise<StateRunResultEnum> {
        return this.runByName(
            this.configuration.getStateName(args),
            args,
            { ...options, fromUrl: true }
        );
    }

    public runByUrl(
        url = this.getCurrentUrl(),
        options: StateRunOptionsGS = {}
    ): Promise<StateRunResultEnum> {
        const args = this.configuration.parseUrl(url);
        if (!args) {
            return this.runByName(
                this.configuration.State404Name,
                {},
                { ...options, notFound: true, fromUrl: true, requestedUrl: url }
            );
        }
        return this.runByUrlArgs(
            args,
            { ...options, fromUrl: true, requestedUrl: url }
        );
    }

    public abstract run(
        constructor: StateConstructor,
        args?: any,
        options?: StateRunOptionsGS
    ): Promise<StateRunResultEnum>;

    public abstract runInstance(
        state: StateGS,
        args?: any,
        options?: StateRunOptionsGS
    ): Promise<StateRunResultEnum>;

    protected getCurrentUrl(): string {
        const scope = RuntimeGS.current;
        if (scope?.url) return scope.url;
        if (scope?.request) return scope.request.url;
        const runtimeWindow = RuntimeGS.window;
        if (runtimeWindow) return runtimeWindow.location.href;
        throw new Error("No URL is available in the current GS state scope.");
    }
}

export class StateRuntimeStoreGS {
    private static readonly _runtimes = new WeakMap<App, StateRuntimeGS>();
    private static readonly _executions = new WeakMap<RuntimeScopeGS, StateExecutionScopeGS>();

    public static has(app: App): boolean {
        return StateRuntimeStoreGS._runtimes.has(app);
    }

    public static set(app: App, runtime: StateRuntimeGS): void {
        if (StateRuntimeStoreGS._runtimes.has(app))
            throw new Error("StateGS is already configured for this application.");
        StateRuntimeStoreGS._runtimes.set(app, runtime);
    }

    public static require(app: App = StateRuntimeStoreGS.currentApp()): StateRuntimeGS {
        const runtime = StateRuntimeStoreGS._runtimes.get(app);
        if (!runtime)
            throw new Error("StateGS is not configured. Configure states during application initialization.");
        return runtime;
    }

    public static execution(scope: RuntimeScopeGS = StateRuntimeStoreGS.currentScope()): StateExecutionScopeGS {
        let execution = StateRuntimeStoreGS._executions.get(scope);
        if (!execution) {
            execution = {};
            StateRuntimeStoreGS._executions.set(scope, execution);
        }
        return execution;
    }

    private static currentApp(): App {
        const app = RuntimeGS.current?.app;
        if (!app) throw new Error("StateGS is unavailable outside an application scope.");
        return app;
    }

    private static currentScope(): RuntimeScopeGS {
        const scope = RuntimeGS.current;
        if (!scope) throw new Error("StateGS is unavailable outside an application scope.");
        return scope;
    }
}
